import type { Lang } from "@/i18n";
import { SLUGS, pageKeyFromLocalizedPath, type PageKey } from "@/lib/route-slugs";

/**
 * Een item is een namespace ("pages") of een deelboom daarvan
 * ("home:cta") wanneer een pagina maar één blok uit een grote namespace
 * nodig heeft.
 *
 * Welke vertaalnamespaces een pagina nodig heeft voor haar eerste render.
 *
 * De SSR-payload zet alleen déze namespaces inline in de HTML. Dat scheelt
 * tientallen KB's per pagina (de volledige NL-set is ~137 KB JSON) en dus
 * FCP/LCP op mobiel. De rest van de taal wordt na de eerste interactie /
 * bij een client-navigatie alsnog geladen via `ensureLang`.
 *
 * Voegt een pagina nieuwe teksten toe uit een andere namespace, dan hoort
 * die namespace hier ook bij — anders verschijnt de sleutel als platte tekst
 * na hydratie.
 */
const CORE = ["common", "nav", "seo"] as const;

/** Gedeeld afsluitend aanvraagblok (zie cta.tsx) op elke pagina. */
const CLOSING = ["contact", "home:cta_advice", "home:showcase", "home:author"] as const;

const PAGE_NS: Record<PageKey, readonly string[]> = {
  home: ["home", "rapport", "contact"],
  diensten: ["pages", "site", "home"],
  pakketten: ["pages", "pkg", "site", "home"],
  proces: ["pages", "site", "home"],
  methode: ["pages", "site", "home"],
  expertise: ["pages", "site", "home"],
  technologie: ["pages", "site", "home"],
  "over-ons": ["pages", "site", "home"],
  analyse: ["analyse", "contact", "home:cta"],
  audit: ["audit", "rapport", "contact", "home:cta"],
  "second-opinion": ["secondopinion", "contact", "home:cta"],
  "voor-bureaus": ["agencies", "contact", "home:cta"],
  contact: ["contact", "pages", "home:cta"],
  voorbeelden: ["home", "pages"],
  // Alleen de woordenlijstteksten (home.packages) — scheelt veel HTML op mobiel.
  woordenlijst: ["home:packages"],
  prijskaart: ["pricing", "home:cta"],
  rapport: ["rapport", "contact", "home:cta"],
  privacy: ["privacy", "home:cta"],
  beveiliging: ["security", "home:cta"],
  realisaties: ["projects", "home:cta"],
  "blog-wat-kost": ["pages", "home:cta"],
};

/** Fallback voor onbekende paden (404, admin, …): alleen de kernnamespaces. */
export function namespacesForPath(rawPath: string, lang: Lang): string[] {
  const pathname = stripLang(rawPath, lang);
  const key = pageKeyFromLocalizedPath(pathname, lang) ?? pageKeyFromBase(pathname, lang);
  const extra = rawPath.startsWith("/admin/funnel")
    ? ["analytics"]
    : rawPath.startsWith("/showcase/homepage")
      ? PAGE_NS.home
      : key ? [...PAGE_NS[key], ...CLOSING] : [];
  return Array.from(new Set([...CORE, ...extra]));
}

function stripLang(path: string, lang: Lang): string {
  if (lang === "nl") return path || "/";
  const rest = path.replace(new RegExp(`^/${lang}(?=/|$)`), "");
  return rest === "" ? "/" : rest;
}

/** Geneste paden (`/woordenlijst/<term>`, `/blog/<slug>`) → basispagina. */
function pageKeyFromBase(pathname: string, lang: Lang): PageKey | undefined {
  let best: PageKey | undefined;
  let bestLength = 0;
  for (const [key, slugs] of Object.entries(SLUGS) as [PageKey, Record<Lang, string>][]) {
    for (const slug of [slugs[lang], slugs.nl]) {
      if (slug !== "/" && pathname.startsWith(`${slug}/`) && slug.length > bestLength) {
        best = key;
        bestLength = slug.length;
      }
    }
  }
  return best;
}

